import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import { KindFilterChip, Pill } from "@/components/infra/InfraParts";
import { captionStyle, cellStyle, pageTitleStyle } from "@/components/infra/infra-styles";
import {
	formatInstanceSize,
	getInfraEnvironments,
	type InfraEnvironments,
	type InfraEnvironmentSummary,
} from "@/lib/api";
import { formatDateTime, formatTime } from "@/lib/format-datetime";
import {
	INFRA_ENVIRONMENT_KINDS,
	INFRA_ENVIRONMENT_KIND_LABELS,
	filterInfraEnvironments,
	formatUptime,
	infraEnvironmentKind,
	infraEnvironmentOrganizations,
	infraEnvironmentSummaryStatus,
	type InfraEnvironmentKind,
} from "@/lib/infra-environments";
import { toggleKind } from "@/lib/infra-placement";

const REFRESH_INTERVAL_MS = 30_000;

/**
 * 管理ページの「環境」タブ (/admin/environments、SuperAdmin のみ)。全組織の環境の種類・稼働時間・
 * ランナーの版を定期的に読み直して見せる。
 */
export function EnvironmentsPage() {
	const [data, setData] = useState<InfraEnvironments | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [selectedKinds, setSelectedKinds] = useState<ReadonlySet<InfraEnvironmentKind>>(new Set());
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
	const mounted = useRef(true);

	const load = useCallback(() => {
		setLoading(true);
		getInfraEnvironments()
			.then((result) => {
				if (!mounted.current) return;
				setData(result);
				setError(null);
			})
			.catch((err: Error) => {
				if (mounted.current) setError(err.message);
			})
			.finally(() => {
				if (mounted.current) setLoading(false);
			});
	}, []);

	useEffect(() => {
		mounted.current = true;
		load();
		const timer = window.setInterval(load, REFRESH_INTERVAL_MS);
		return () => {
			mounted.current = false;
			window.clearInterval(timer);
		};
	}, [load]);

	const all = data?.environments ?? [];
	const organizations = infraEnvironmentOrganizations(all);
	// 選んだ組織の環境が読み直しで無くなったら、全組織の表示に戻す。
	const organizationId = organizations.some((o) => o.id === selectedOrganizationId)
		? selectedOrganizationId
		: null;
	const inOrganization = filterInfraEnvironments(all, new Set(), organizationId);
	const shown = filterInfraEnvironments(all, selectedKinds, organizationId);

	return (
		<div
			data-testid="environments-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2 style={pageTitleStyle}>環境</h2>
			<p style={captionStyle({ margin: "6px 0 16px" })}>
				全組織の環境です。稼働中の環境を稼働時間の長い順に並べます。
				{REFRESH_INTERVAL_MS / 1000} 秒ごとに読み直します。
				{data !== null && ` 最終更新: ${formatTime(data.observedAt)}`}
			</p>

			{error !== null && (
				<p
					data-testid="environments-error"
					style={{ margin: "0 0 16px", fontSize: "var(--text-ui)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{error === null && data === null && <p style={captionStyle({ margin: 0 })}>読み込み中…</p>}

			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 8,
					marginBottom: 24,
				}}
			>
				{data !== null && (
					<>
						{INFRA_ENVIRONMENT_KINDS.map((kind) => (
							<KindFilterChip
								key={kind}
								label={INFRA_ENVIRONMENT_KIND_LABELS[kind]}
								testId={`environments-filter-${kind}`}
								value={inOrganization.filter((e) => infraEnvironmentKind(e) === kind).length}
								selected={selectedKinds.has(kind)}
								onClick={() => setSelectedKinds((current) => toggleKind(current, kind))}
							/>
						))}
						<select
							className="ds-select"
							data-testid="environments-organization-filter"
							value={organizationId ?? ""}
							onChange={(e) => setSelectedOrganizationId(e.target.value || null)}
							style={{ width: 240, height: 32, marginLeft: "auto" }}
						>
							<option value="">すべての組織</option>
							{organizations.map((organization) => (
								<option key={organization.id} value={organization.id}>
									{organization.name} ({organization.environmentCount})
								</option>
							))}
						</select>
					</>
				)}
				<IconButton
					icon="rotate"
					title="環境を更新"
					data-testid="environments-refresh"
					size={26}
					disabled={loading}
					onClick={load}
					style={{ marginLeft: data === null ? "auto" : undefined }}
				/>
			</div>

			{data !== null &&
				(shown.length === 0 ? (
					<p style={captionStyle({ margin: 0 })}>環境はありません。</p>
				) : (
					<EnvironmentTable environments={shown} observedAt={data.observedAt} />
				))}
		</div>
	);
}

function EnvironmentTable({
	environments,
	observedAt,
}: {
	environments: InfraEnvironmentSummary[];
	observedAt: string;
}) {
	const now = new Date(observedAt).getTime();
	return (
		<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
			<thead>
				<tr style={captionStyle({ textAlign: "left" })}>
					<th style={{ ...cellStyle, fontWeight: 500 }}>環境</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: "18%" }}>組織</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 88 }}>種類</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 96 }}>状態</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 112 }}>稼働時間</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: "18%" }}>ランナー</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 80 }}>サイズ</th>
					<th style={{ ...cellStyle, fontWeight: 500, width: 144 }}>作成日時</th>
				</tr>
			</thead>
			<tbody>
				{environments.map((environment) => {
					const status = infraEnvironmentSummaryStatus(environment);
					return (
						<tr key={environment.id} data-testid={`environments-row-${environment.id}`}>
							<td style={cellStyle}>
								<TruncatedLine text={environment.name} title={environment.id} strong />
								<TruncatedLine text={environment.projectName} title={environment.projectName} />
							</td>
							<td style={cellStyle}>
								<TruncatedLine
									text={environment.organizationName}
									title={environment.organizationName}
									strong
								/>
							</td>
							<td style={cellStyle}>
								<Pill label={INFRA_ENVIRONMENT_KIND_LABELS[infraEnvironmentKind(environment)]} />
							</td>
							<td style={cellStyle}>
								<Pill label={status.label} tone={status.tone} />
							</td>
							<td style={captionStyle(cellStyle)}>
								{environment.startedAt === null
									? "—"
									: formatUptime(now - new Date(environment.startedAt).getTime())}
							</td>
							<td style={cellStyle}>
								{environment.runner === null ? (
									<span style={captionStyle()}>—</span>
								) : (
									<>
										<TruncatedLine
											text={environment.runner.runnerVersion ?? "版の報告なし"}
											title={environment.runner.id}
											strong
										/>
										<TruncatedLine text={environment.runner.name} title={environment.runner.name} />
									</>
								)}
							</td>
							<td style={captionStyle(cellStyle)}>
								{environment.instanceSize === null
									? "—"
									: formatInstanceSize(environment.instanceSize)}
							</td>
							<td style={captionStyle(cellStyle)}>{formatDateTime(environment.createdAt)}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

/** 1 行に収め、はみ出すときだけ title で全文を見せる。strong は主の値 (濃い色)。 */
function TruncatedLine({ text, title, strong }: { text: string; title: string; strong?: boolean }) {
	const style = {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	} as const;
	if (strong)
		return (
			<div
				title={title}
				data-tooltip-if-truncated={title === text ? true : undefined}
				style={{ ...style, fontSize: "var(--text-caption)", color: "var(--fg-1)" }}
			>
				{text}
			</div>
		);
	return (
		<div title={title} data-tooltip-if-truncated style={captionStyle(style)}>
			{text}
		</div>
	);
}
